import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function AuthPage() {
  const [mode, setMode] = useState("signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithPassword, signupWithPassword } = useAuth();
  const navigate = useNavigate();

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signin") {
        await loginWithPassword(email, password);
        toast.success("Welcome back.");
      } else {
        if (!name.trim()) { toast.error("Please add your name."); setLoading(false); return; }
        await signupWithPassword(email, password, name);
        toast.success("Welcome to the studio.");
      }
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-[80vh] max-w-[900px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-2 gap-16">
      <div className="hidden md:block">
        <div className="overline text-[var(--gu-muted)] mb-6">Members</div>
        <h1 className="font-display text-5xl font-light tracking-tighter leading-[0.95]">Sign in to<br />the studio.</h1>
        <p className="mt-8 text-[var(--gu-muted)] leading-relaxed">
          Track your orders, keep a wishlist, and (soon) tend your own storefront.
        </p>
      </div>

      <div>
        <div className="flex gap-6 mb-10 overline">
          <button onClick={() => setMode("signin")} data-testid="tab-signin" className={`pb-2 border-b ${mode === "signin" ? "border-[var(--gu-primary)] text-[var(--gu-primary)]" : "border-transparent text-[var(--gu-muted)]"}`}>Sign in</button>
          <button onClick={() => setMode("signup")} data-testid="tab-signup" className={`pb-2 border-b ${mode === "signup" ? "border-[var(--gu-primary)] text-[var(--gu-primary)]" : "border-transparent text-[var(--gu-muted)]"}`}>Create account</button>
        </div>

        <button onClick={googleSignIn} data-testid="google-signin-btn" className="w-full border border-[var(--gu-border)] py-4 overline hover:bg-[var(--gu-surface-2)] transition-colors flex items-center justify-center gap-3">
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.7 0 6.7 5.4 2.7 13.3l7.9 6.1C12.6 13.6 17.9 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.6 3-2.3 5.6-4.9 7.3l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.6 28.6c-1.1-3.1-1.1-6.5 0-9.6l-7.9-6.1C.5 17 0 20.4 0 24s.5 7 2.7 11.1l7.9-6.5z"/><path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.6-5.9c-2.1 1.4-4.8 2.3-7.6 2.3-6.1 0-11.4-4.1-13.4-9.6l-7.9 6.5C6.7 42.6 14.7 48 24 48z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 my-8 text-xs text-[var(--gu-muted)] uppercase tracking-widest-2">
          <div className="flex-1 h-px bg-[var(--gu-border)]" />
          or
          <div className="flex-1 h-px bg-[var(--gu-border)]" />
        </div>

        <form onSubmit={submit} className="space-y-6">
          {mode === "signup" && (
            <Field label="Your name" value={name} onChange={(e) => setName(e.target.value)} testId="input-name" />
          )}
          <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testId="input-email" />
          <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} testId="input-password" />
          <button type="submit" disabled={loading} data-testid="submit-auth-btn" className="w-full bg-[var(--gu-primary)] text-[var(--gu-bg)] py-4 overline hover:bg-[var(--gu-primary)]/90 transition-colors disabled:opacity-60">
            {loading ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <Link to="/" className="mt-8 inline-block text-sm text-[var(--gu-muted)] hover:text-[var(--gu-primary)]">← Back to home</Link>
      </div>
    </div>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <label className="block">
      <span className="overline text-[var(--gu-muted)] block mb-2">{label}</span>
      <input {...rest} data-testid={testId} className="w-full bg-transparent border-b border-[var(--gu-border)] focus:border-[var(--gu-primary)] outline-none py-2 text-lg font-body transition-colors" />
    </label>
  );
}
